import React, {
  useState, useRef,
} from 'react';
import GoogleMapReact from 'google-map-react';
import styled from 'styled-components';
import tw from 'tailwind.macro';
import * as turf from '@turf/turf';
import useSupercluster from 'use-supercluster';
import { useQueryParam, NumberParam, StringParam } from "use-query-params";


import MapInfoWindow from './MapInfoWindow';
import openIcon from '../images/on.png';
import closeIcon from '../images/off.png';

import open24hIcon from '../images/icon_card-24h.png';
import close24hIcon from '../images/icon-pharma-24h-closed.png';
import geoIcon from '../images/icon-geo.png';
import locationIcon from '../images/location.png';
import clusterIconSmall from '../images/cluster/01.png';
import clusterIconLarge from '../images/cluster/02.png';
import mapStyle from '../style/map.json';

const defaultOptions = {
  center: {
    lat: -33.4369564,
    lng: -70.6343399,
  },
  zoom: 15,
  style: mapStyle,
};

export default ({
  locals, nearestLocalsFromPoint, actualLocation, center, setCenter,
  setMapOrAddressActive, zoom, setZoom, setActualViewportMap, locationType,
  selectedLocalID, setSelectedLocalID,
}) => {
  const [bounds, setBounds] = useState(null);
  const mapRef = useRef();

  const handleClickMarker = (localID) => setSelectedLocalID(localID);
  const cleanInfoWindow = () => setSelectedLocalID();
  const handleClickMap = () => cleanInfoWindow();
  const handleCloseInfoWindow = () => cleanInfoWindow();
  const handleDragEnd = () => setMapOrAddressActive(true);


  const [latitud, setLat] = useQueryParam("lat", NumberParam);
  const [longitud, setLng] = useQueryParam("lng", NumberParam);

  defaultOptions.center.lat = latitud ? latitud : -33.4369564;
  defaultOptions.center.lng = longitud ? longitud : -70.6343399;

  const onReady = ({ map }) => {
    if (map) console.log('Map ready!');

    // To load autocomplete input
    if (window.callbackAutocomplete) {
      console.log('Callback autocomplete called!');
      window.callbackAutocomplete();
    }

    mapRef.current = map;
  };

  const handleZoom = (newZoom) => {
    setMapOrAddressActive(true);
    setZoom(newZoom);
  };

  const handleBoundsChanged = ({ bounds }) => {
    const topLeft = {
      lat: bounds.nw.lat,
      lng: bounds.nw.lng,
    };
    const bottomRight = {
      lat: bounds.se.lat,
      lng: bounds.se.lng,
    };
    const polygon = [[
      [topLeft.lng, topLeft.lat],
      [topLeft.lng, bottomRight.lat],
      [bottomRight.lng, bottomRight.lat],
      [bottomRight.lng, topLeft.lat],
      [topLeft.lng, topLeft.lat],
    ]];

    console.log('Update map bounds and viewport');
    setActualViewportMap(polygon);
    setBounds([
      bounds.nw.lng,
      bounds.se.lat,
      bounds.se.lng,
      bounds.nw.lat,
    ]);

    //if map change update center
    if (mapRef.current) {
       setCenter();
    }

  };

  const getNearestLocal = () => {
    // Show only if actualLocation is setted
    if (actualLocation) {
      const point = turf.point([actualLocation.lng, actualLocation.lat]);
      const nearestLocals = nearestLocalsFromPoint(point, 20, 1, true);
      return nearestLocals.length > 0 ? nearestLocals[0] : null;
    }
    return null;
  };

  const chooseIcon = (local) => {
    if (local.is24h) {
      return local.abierto ? open24hIcon : close24hIcon;
    }
    return local.abierto ? openIcon : closeIcon;
  };

  const centerSearchMarker = () => {
    let icon;

    if (!actualLocation) return null;
    if (!locationType) return null;

    if (locationType === 'geo') {
      icon = geoIcon;
    } else {
      icon = locationIcon;
    }

    return (
      <Marker
        lat={actualLocation.lat}
        lng={actualLocation.lng}
        title={locationType === 'geo' ? 'Ubicación actual' : 'Dirección buscada'}
      >
        <Img src={icon} alt="" width="30" />
      </Marker>
    );
  };


  // Points for cluster
  const points = locals.map((local) => ({
    type: 'Feature',
    properties: { cluster: false, local },
    geometry: {
      type: 'Point',
      coordinates: [
        parseFloat(local.coordenadaObj.lng),
        parseFloat(local.coordenadaObj.lat),
      ],
    },
  }));

  const icons = [clusterIconSmall, clusterIconLarge]

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 100, maxZoom: 14 },
  });

  return (
    <GoogleMapReact
      yesIWantToUseGoogleMapApiInternals
      bootstrapURLKeys={{
        key: 'AIzaSyDrHkVGWnM4ti4sVHrCSSw5bwWDQQuD2e0',
        libraries: ['places'],
      }}
      options={{
        zoomControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        styles: mapStyle,
      }}
      defaultCenter={defaultOptions.center}
      defaultZoom={defaultOptions.zoom}
      center={center}
      zoom={zoom}
      onGoogleApiLoaded={onReady}
      onDragEnd={handleDragEnd}
      onZoomAnimationEnd={handleZoom}
      onChange={handleBoundsChanged}
      onClick={handleClickMap}
      onChildClick={handleClickMarker}
    >

      {clusters.map((cluster) => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const {
          cluster: isCluster,
          point_count: pointCount,
          local,
        } = cluster.properties;

        if (isCluster) {
          return (
            <Marker
              key={`cluster-${cluster.id}`}
              lat={latitude}
              lng={longitude}
            >
              <ClusterMarker
                icons={icons}
                pointsTotal={points.length}
                pointsHere={pointCount}
                onClick={() => {
                  const expansionZoom = Math.min(
                    supercluster.getClusterExpansionZoom(cluster.id),
                    20,
                  );
                  mapRef.current.setZoom(expansionZoom);
                  mapRef.current.panTo({ lat: latitude, lng: longitude });
                }}
              >
                {pointCount}
              </ClusterMarker>
            </Marker>
          );
        }

        return (
          <Marker
            key={local.local}
            lat={local.coordenadaObj.lat}
            lng={local.coordenadaObj.lng}
            title={local.direccion}
          >
            {/* {nearestLocal && nearestLocal.local === local.local ? (
            <img src={chooseIcon(local)} alt="" width="50" />
          ) : ( */}
            <Img src={chooseIcon(local)} alt="" width="30" />
            {/* ) } */}

            {local.local === selectedLocalID && (
            <MapInfoWindow
              local={local}
              nearestLocalsFromPoint={nearestLocalsFromPoint}
              onClose={handleCloseInfoWindow}
              onSelectNearLocal={setSelectedLocalID}
            />
            )}
          </Marker>
        );
      })}

      {centerSearchMarker()}
      
    </GoogleMapReact>
  );
};

const Img = styled.img`
  margin-top: -32px;
  margin-left: -17px;
`;

const Marker = styled.div`
`;

const ClusterMarker = styled.div`
  background-image: url(${props =>
    (props.pointsHere <= 10  && props.icons[0]) ||
    (props.pointsHere > 10 && props.icons[1])
  });
  background-size: cover;
  color: #FFFFFF;
  width: ${(props) => 5 + (props.pointsHere / props.pointsTotal) * 150}px;
  height: ${(props) => 5 + (props.pointsHere / props.pointsTotal) * 150}px;

  ${tw`
    flex items-center justify-center
    rounded-full p-3 shadow font-bold text-xs
  `}
`;
